import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { Page, ResponseError } from '../type/type';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';

export interface IFortuneLabCode {
  id: number;
  trkId: string;
  campaignName: string;
  couponName?: string;
  linkSlug?: string;
  landingImageUrls?: string[];
  companyName?: string;
  bizProfileName?: string;
  franchiseName?: string;
  createdAt: string;
  updatedAt: string;
  landedUserCount: number;
  resultUserCount: number;
  sendYn?: boolean;
}

export interface IFortuneLabCodeCreateRequest {
  campaignName: string;
  couponName?: string;
  linkSlug?: string;
  landingImageUrls?: string[];
  sendYn: boolean;
}

interface FortuneLabCodeResponse extends Page {
  content: IFortuneLabCode[];
}

type FortuneLabCodeState = {
  codeList: IFortuneLabCode[];
  codePage: Page | null;
  loading: boolean;
};

type FortuneLabCodeAction = {
  fetchCodes: (page?: number, size?: number) => Promise<void>;
  createCode: (payload: IFortuneLabCodeCreateRequest) => Promise<void>;
};

export const useFortuneLabCodeStore = create<
  FortuneLabCodeState & FortuneLabCodeAction
>((set, get) => ({
  codeList: [],
  codePage: null,
  loading: false,

  fetchCodes: async (page = 0, size = 20) => {
    set({ loading: true });
    try {
      const response = await apiClient.get<FortuneLabCodeResponse>(
        '/fortune-lab/tracking',
        {
          params: {
            page,
            size,
            sort: 'createdAt,desc',
          },
        }
      );
      const { content, ...rest } = response.data;
      set({ codeList: content, codePage: rest });
    } catch (error) {
      console.error(error);
      toast.error('트래킹 코드를 불러오지 못했습니다.');
    }
    set({ loading: false });
  },

  createCode: async (payload: IFortuneLabCodeCreateRequest) => {
    if (!payload.campaignName.trim()) {
      toast.error('캠페인명을 입력해주세요.');
      return;
    }
    if (payload.sendYn) {
      if (!payload.couponName?.trim()) {
        toast.error('쿠폰명을 입력해주세요.');
        return;
      }
      if (!payload.linkSlug?.trim()) {
        toast.error('쿠폰 링크를 입력해주세요.');
        return;
      }
    }
    try {
      await apiClient.post('/fortune-lab/tracking', payload);
      toast.success('새로운 트래킹 코드가 생성되었습니다.');
      const { codePage } = get();
      const pageNumber = codePage?.pageable?.pageNumber ?? 0;
      await get().fetchCodes(pageNumber);
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg ?? '코드 생성에 실패했습니다.');
      } else {
        console.error(error);
        toast.error('코드 생성에 실패했습니다.');
      }
    }
  },
}));
